// Page-view scheduling for PHProvider. PostHog stays off the critical path:
// with analytics consent, the analytics client is imported when the page is
// idle, on the first user interaction, or when the page is hidden, whichever
// comes first; without it, only once consent is granted. This module must
// never import "@/src/analytics/client" statically.

export type DeferredAnalyticsClient = {
  initializeClientAnalytics(): boolean | void;
  captureClientPageView(pathname: string): boolean;
  subscribeToClientAnalyticsPreference(listener: () => void): () => void;
};

export type AnalyticsClientLoader = () => Promise<DeferredAnalyticsClient>;

/** Memoizes the import; a rejected import is forgotten so the next call retries. */
export function createAnalyticsClientLoader(importer: AnalyticsClientLoader): AnalyticsClientLoader {
  let pending: Promise<DeferredAnalyticsClient> | null = null;
  return () => {
    pending ??= importer().catch((error: unknown) => {
      pending = null;
      throw error;
    });
    return pending;
  };
}

export function isWorkspacePath(pathname: string): boolean {
  return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
}

/** Per-provider state that survives effect re-runs. */
export type PageViewTracker = {
  previousPathname: string | null;
};

export function createPageViewTracker(): PageViewTracker {
  return { previousPathname: null };
}

/** The "analytics" consent decision (consent, privacy signal and HIPAA mode). */
export type AnalyticsConsentGate = {
  allowed(): boolean;
  subscribe(listener: () => void): () => void;
};

type ListenerTarget = Pick<EventTarget, "addEventListener" | "removeEventListener">;

const FLUSH_EVENTS = ["pointerdown", "keydown", "pagehide"] as const;

export function schedulePageView({
  pathname,
  tracker,
  load,
  whenIdle,
  consent,
  target,
}: {
  pathname: string | null;
  tracker: PageViewTracker;
  load: AnalyticsClientLoader;
  whenIdle: (callback: () => void) => () => void;
  consent: AnalyticsConsentGate;
  target?: ListenerTarget | null;
}): () => void {
  // useAuthStatus owns every workspace pageview (after WorkOS verification and
  // on client-side re-entry); capturing one here too would double count it,
  // e.g. on the /auth/callback -> /dashboard replace.
  const capturable = pathname !== null && !isWorkspacePath(pathname);

  let active = true;
  let pending = true;
  let viewHandled = false;
  let waitingForConsent = false;
  let unsubscribe: (() => void) | null = null;
  let unsubscribeConsent: (() => void) | null = null;
  let cancelIdle: (() => void) | null = null;

  const capture = (analytics: DeferredAnalyticsClient) => {
    if (!capturable || tracker.previousPathname === pathname) return;
    tracker.previousPathname = analytics.captureClientPageView(pathname) ? pathname : null;
  };

  const captureView = (analytics: DeferredAnalyticsClient) => {
    if (viewHandled) return;
    viewHandled = true;
    analytics.initializeClientAnalytics();
    capture(analytics);
  };

  const start = (analytics: DeferredAnalyticsClient) => {
    captureView(analytics);
    if (!active) return;
    // Only a grant (analytics off -> on) records this view again. A withdrawal,
    // a diagnostics-only change or a compliance update records nothing.
    let wasAllowed = consent.allowed();
    unsubscribe = analytics.subscribeToClientAnalyticsPreference(() => {
      const allowed = consent.allowed();
      const granted = allowed && !wasAllowed;
      wasAllowed = allowed;
      if (!granted) return;
      tracker.previousPathname = null;
      capture(analytics);
    });
  };

  const removeListeners = () => {
    for (const type of FLUSH_EVENTS) target?.removeEventListener(type, run, true);
  };

  function run() {
    if (!pending) return;
    pending = false;
    cancelIdle?.();
    removeListeners();
    void load().then(start, () => {
      // Analytics must never prevent the app from starting.
    });
  }

  if (consent.allowed()) {
    cancelIdle = whenIdle(run);
    for (const type of FLUSH_EVENTS) target?.addEventListener(type, run, true);
  } else {
    // Without analytics consent the PostHog chunk is never downloaded. A grant
    // on this page loads it at once and records this view.
    waitingForConsent = true;
    unsubscribeConsent = consent.subscribe(() => {
      if (!consent.allowed()) return;
      waitingForConsent = false;
      unsubscribeConsent?.();
      unsubscribeConsent = null;
      run();
    });
  }

  return () => {
    active = false;
    unsubscribeConsent?.();
    // Flush rather than drop: a quick exit (e.g. Get started -> WorkOS) or a
    // navigation before idle still records this pathname's view. Nothing to
    // flush while consent is still pending.
    if (!waitingForConsent) run();
    unsubscribe?.();
  };
}
